import { useTranslation } from 'react-i18next'

import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import { listRoutableLearnArticleDefinitions } from '@/features/aprender/articleBodies'
import { learnArticlePath, localizeLearnArticle } from '@/features/aprender/articles'
import { LEARN_TOPIC_TABS, type LearnTopicTabKey } from '@/features/aprender/learnTopicTabs'
import { LocaleLink } from '@/navigation/LocaleLink'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { articleHub } from '@/shared/lib/articleHubStyles'
import { DEFAULT_SHARE_IMAGE } from '@/shared/lib/siteSeo'
import { ArticleHubListing } from '@/shared/ui/ArticleHubListing'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import { SocialShareMeta } from '@/shared/ui/SocialShareMeta'
import '@/styles/food-cost-fonts.css'

type LearnTopicKey = 'Escandallos' | 'Beneficio' | 'Inventario' | 'Emprender en hostelería'

const TOPIC_META: Record<
    LearnTopicKey,
    {
        categoryKey: LearnTopicKey
        route: string
        tabKey: Exclude<LearnTopicTabKey, 'all'>
        esLabel: string
        enLabel: string
        esIntro: string
        enIntro: string
        esEyebrow: string
        enEyebrow: string
        esIntroLinkBefore?: string
        esIntroLinkLabel?: string
        esIntroLinkAfter?: string
        enIntroLinkBefore?: string
        enIntroLinkLabel?: string
        enIntroLinkAfter?: string
        definitionArticleId?: string
    }
> = {
    Escandallos: {
        categoryKey: 'Escandallos',
        route: ROUTES.learnEscandallos,
        tabKey: 'esc',
        esLabel: 'Escandallos',
        enLabel: 'Recipe costing',
        esEyebrow: 'Aprender',
        enEyebrow: 'Learn',
        esIntro:
            'Guías para calcular cuánto cuesta realmente un plato y convertir el escandallo en una decisión diaria.',
        enIntro: 'Guides to calculate what each dish really costs and turn costing into a daily decision.',
        esIntroLinkBefore: 'Empieza por ',
        esIntroLinkLabel: 'qué es un escandallo',
        esIntroLinkAfter: ': la definición, qué no es y por qué caduca con cada albarán.',
        enIntroLinkBefore: 'Start with ',
        enIntroLinkLabel: 'what a recipe costing is',
        enIntroLinkAfter: ': the definition, what it is not, and why it expires with every delivery note.',
        definitionArticleId: 'que-es-un-escandallo',
    },
    Beneficio: {
        categoryKey: 'Beneficio',
        route: ROUTES.learnBeneficio,
        tabKey: 'ben',
        esLabel: 'Beneficio',
        enLabel: 'Profit',
        esEyebrow: 'Aprender',
        enEyebrow: 'Learn',
        esIntro: 'Ritual de números: food cost, margen y decisiones que protegen tu beneficio mes a mes.',
        enIntro: 'A numbers routine: food cost, margin and decisions that protect your profit month after month.',
    },
    Inventario: {
        categoryKey: 'Inventario',
        route: ROUTES.learnInventario,
        tabKey: 'inv',
        esLabel: 'Inventario',
        enLabel: 'Inventory',
        esEyebrow: 'Aprender',
        enEyebrow: 'Learn',
        esIntro:
            'Contar el almacén (20–30 referencias), entradas correctas (albaranes) y desviación (teórico vs real) para que tu coste no te engañe.',
        enIntro:
            'Count the cooler (20–30 SKUs), correct inputs (delivery notes) and the deviation (theoretical vs actual) so your cost doesn’t lie to you.',
    },
    'Emprender en hostelería': {
        categoryKey: 'Emprender en hostelería',
        route: ROUTES.learnEmprenderEnHosteleria,
        tabKey: 'emprender',
        esLabel: 'Emprender en hostelería',
        enLabel: 'Starting in hospitality',
        esEyebrow: 'Aprender',
        enEyebrow: 'Learn',
        esIntro:
            'Paso a paso desde que tienes una idea hasta que abres la persiana: viabilidad, medios y decisiones antes de firmar nada.',
        enIntro: 'Step by step from idea to opening day: viability, means and decisions before you sign anything.',
    },
}

export function LearnTopicPage({ topic }: { topic: LearnTopicKey }) {
    const { t, i18n } = useTranslation()
    const locale = useLandingLocale()

    const meta = TOPIC_META[topic]
    const isEn = locale === 'en'

    const title = isEn ? `${meta.enLabel} | Learn with Tipi` : `${meta.esLabel} | Aprende con Tipi`
    const description = isEn ? meta.enIntro : meta.esIntro
    const canonicalUrl = absoluteLocalizedUrl(meta.route, locale)

    const articles = listRoutableLearnArticleDefinitions()
        .filter(article => article.category === meta.categoryKey)
        .map(article => localizeLearnArticle(article, locale))

    return (
        <div className={articleHub.page}>
            <LocaleSeoLinks path={meta.route} locale={locale} />
            <SocialShareMeta
                title={title}
                description={description}
                imageUrl={DEFAULT_SHARE_IMAGE}
                url={canonicalUrl}
            />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
            </PageHelmet>

            <SubpageFoodCostHeader />

            <main className={articleHub.main}>
                <div className={articleHub.header}>
                    <div className={articleHub.headerCopy}>
                        <div className={articleHub.eyebrow}>{isEn ? meta.enEyebrow : meta.esEyebrow}</div>
                        <h1 className={articleHub.title}>{isEn ? meta.enLabel : meta.esLabel}</h1>
                    </div>
                    <p className={articleHub.intro}>
                        {description}
                        {meta.definitionArticleId ? (
                            <>
                                {' '}
                                {isEn ? meta.enIntroLinkBefore : meta.esIntroLinkBefore}
                                <LocaleLink
                                    to={learnArticlePath(meta.definitionArticleId)}
                                    className='font-semibold text-article-flame no-underline hover:underline'
                                >
                                    {isEn ? meta.enIntroLinkLabel : meta.esIntroLinkLabel}
                                </LocaleLink>
                                {isEn ? meta.enIntroLinkAfter : meta.esIntroLinkAfter}
                            </>
                        ) : null}
                    </p>
                </div>

                <nav className={articleHub.tabs}>
                    {LEARN_TOPIC_TABS.map(tab => {
                        const label = isEn ? tab.labelEn : tab.labelEs
                        const isActive = tab.key === meta.tabKey
                        return (
                            <LocaleLink
                                key={tab.key}
                                to={tab.to}
                                className={isActive ? articleHub.tabActive : articleHub.tabInactive}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                {label}
                            </LocaleLink>
                        )
                    })}
                </nav>

                <ArticleHubListing
                    articles={articles}
                    articlePath={learnArticlePath}
                    locale={i18n.language}
                    readingMinutesLabel={count =>
                        t('learnPage.readingMinutes', {
                            count,
                            defaultValue: `${count} min de lectura`,
                        })
                    }
                    readArticleLabel={t('learnPage.readArticle')}
                />
            </main>

            <Footer landingPage='learn' className='border-article-cream/10 bg-article-shell' />
        </div>
    )
}
